import React, {
  Dispatch,
  useContext,
  createContext,
  ReactNode,
  useReducer,
  useEffect,
} from 'react';

// Types
import {
  InfringementTableDataAction,
  InfringementTableDataState,
  CorrelatedLocationState,
} from './types';

// Constants
import { filterKeyMap, INFRINGEMENTS_QUERY_FIRST } from './constants';
import { OrderByDirectionEnum, InfringementSummaryFilter } from 'src/app/gql/__generated__/hooks';
import { infringementDataReducer } from './reducer';
import { useLocation, useHistory } from 'react-router-dom';
import { useParseUrlFilters, serializeFiltersToUrl } from 'src/utils/filters/parseUrlFilters';
import { useSessionStorage } from 'src/utils/hooks';

const createInitialState = (
  correlatedIds: number[] = [],
  filterInitialState: InfringementSummaryFilter = {},
  sessionAcids?: string[]
): InfringementTableDataState => {
  const finalFilter = {
    ...filterInitialState,
    ...(sessionAcids && sessionAcids.length > 0 ? { acids: sessionAcids } : {}),
    ...(correlatedIds.length > 0 ? { limitToIds: correlatedIds } : {}),
  };

  return {
    variables: {
      filter: finalFilter,
      includeOperationTags: true,
      first: INFRINGEMENTS_QUERY_FIRST,
      sort: [
        {
          field: 'time',
          direction: OrderByDirectionEnum.Asc,
        },
      ],
    },
    hasCandidatesEnabled: false,
    hasCorrelated: false,
    loading: false,
    error: undefined,
  };
};

const InfringementTableDataContext = createContext<{
  state: InfringementTableDataState;
  dispatch: Dispatch<InfringementTableDataAction>;
}>({
  state: createInitialState(),
  dispatch: () => undefined,
});

export const InfringementDataTableProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const location = useLocation<CorrelatedLocationState>();
  const history = useHistory();
  const locationState = location.state;
  const correlatedIds = locationState?.ids || [];

  const filterInitialState: InfringementSummaryFilter = useParseUrlFilters<
    InfringementSummaryFilter
  >(filterKeyMap);

  // Use session storage for acid values if no URL filter acids are provided
  const [storedAcid] = useSessionStorage('', 'acidSearchValue');

  const getSessionAcids = (): string[] | undefined => {
    if (filterInitialState.acids && filterInitialState.acids.length > 0) {
      return undefined; // Use URL filter values
    }

    if (typeof storedAcid === 'string' && storedAcid.trim()) {
      const acids = storedAcid.split(',').map(acid => acid.trim().toUpperCase());
      return acids.length > 0 ? acids : undefined;
    }
    return undefined;
  };

  const sessionAcids = getSessionAcids();
  const [state, dispatch] = useReducer(
    infringementDataReducer,
    createInitialState(correlatedIds, filterInitialState, sessionAcids)
  );

  // Update URL whenever filter state changes
  useEffect(() => {
    // Skip URL updates if we have correlated IDs (navigation state)
    if (correlatedIds.length > 0) {
      return;
    }

    // Create a clean filter object excluding internal fields
    const filterForUrl = { ...state.variables.filter };
    delete filterForUrl.limitToIds; // Don't serialize internal navigation state
    const urlSearchParams = serializeFiltersToUrl(filterForUrl, filterKeyMap, true);
    const currentSearch = location.search.substring(1); // Remove leading '?'

    // Only update URL if the search params have actually changed
    if (urlSearchParams !== currentSearch) {
      const newPath = `${location.pathname}${urlSearchParams ? `?${urlSearchParams}` : ''}`;
      history.replace(newPath, location.state);
    }
  }, [
    state.variables.filter,
    correlatedIds,
    location.pathname,
    location.search,
    location.state,
    history,
  ]);

  return (
    <InfringementTableDataContext.Provider value={{ state, dispatch }}>
      {children}
    </InfringementTableDataContext.Provider>
  );
};

export const useInfringementDataTable = (): {
  state: InfringementTableDataState;
  dispatch: Dispatch<InfringementTableDataAction>;
} => {
  const context = useContext(InfringementTableDataContext);
  if (!context) {
    throw new Error(
      'useInfringementDataTable must be used within an InfringementDataTableProvider'
    );
  }
  return context;
};
